import React from "react";
import {Device, DeviceField} from "../../../app/api/model/device";
import {useTranslation} from "react-i18next";
import {ListRequest, useDeviceCreateMutation, useHardwareTypeListQuery} from "../../../app/api/api";
import * as yup from "yup";
import {TFunction} from "i18next";
import {UseModal} from "../../../hooks/useModal";
import UpdateDialog from "../../../components/Dialog/UpdateDialog";
import {InputField, InputFieldType} from "../../../components/input/factory/types";

export interface AddEditDeviceDialogParams {
    modal: UseModal;
    device?: Device;
    onSuccess?: () => void;
}

export default function AddEditDeviceDialog({device, modal, onSuccess}: AddEditDeviceDialogParams) {
    const {t} = useTranslation();
    const [createDevice] = useDeviceCreateMutation();

    const isCreate = !device?.[DeviceField.ID];

    const handleSubmit = async (d: Partial<Device>) => {
        if (!d?.[DeviceField.ID]) {
            const request = {
                [DeviceField.ICCID]: d[DeviceField.ICCID],
                [DeviceField.IMEI]: d[DeviceField.IMEI],
                [DeviceField.MSISDN]: d[DeviceField.MSISDN],
                [DeviceField.IMSI]: d[DeviceField.IMSI],
                [DeviceField.HARDWARE_TYPE]: d[DeviceField.HARDWARE_TYPE],
            } as Partial<Device>;
            const response = await createDevice(request).unwrap();
            return response.id;
        }
        //else {
        // // TODO: implement update once it is supported by the backend
        // }
        return 0;
    };

    const getValidationSchema = (t: TFunction) =>
        yup.object({
            [DeviceField.ICCID]: yup.string().min(19).max(20).required(t("validation_inputRequired")), //validation 19-20 digits
            [DeviceField.IMEI]: yup.string().required(t("validation_inputRequired")),
            [DeviceField.MSISDN]: yup.string().required(t("validation_inputRequired")),
            [DeviceField.IMSI]: yup.string().required(t("validation_inputRequired")),
            [DeviceField.HARDWARE_TYPE]: yup.string().required(t("validation_inputRequired")),
        });

    const initialValues = {
        [DeviceField.ID]: device?.[DeviceField.ID] || undefined,
        [DeviceField.ICCID]: device?.[DeviceField.ICCID] || "",
        [DeviceField.IMEI]: device?.[DeviceField.IMEI] || "",
        [DeviceField.MSISDN]: device?.[DeviceField.MSISDN] || "",
        [DeviceField.IMSI]: device?.[DeviceField.IMSI] || "",
        [DeviceField.HARDWARE_TYPE]: device?.[DeviceField.HARDWARE_TYPE] || "",
        [DeviceField.SERIAL_NUMBER]: device?.[DeviceField.SERIAL_NUMBER] || "",
        [DeviceField.ONLINE]: device?.[DeviceField.ONLINE] || undefined,
        [DeviceField.LAST_CONNECTION_AT]: device?.[DeviceField.LAST_CONNECTION_AT] || "",
        [DeviceField.CURRENT_FIRMWARE]: device?.[DeviceField.CURRENT_FIRMWARE] || undefined,
        [DeviceField.TARGET_FIRMWARE]: device?.[DeviceField.TARGET_FIRMWARE] || undefined,
    };

    return (
        <UpdateDialog
            titleKey={device ? "action_editDevice" : "action_createDevice"}
            modal={modal}
            validationSchema={getValidationSchema}
            inputFields={[
                {
                    fieldName: DeviceField.ID,
                    type: InputFieldType.HIDDEN,
                    required: !isCreate,
                    translationKey: t("generic_id"),
                },
                {
                    translationKey: "generic_iccid",
                    fieldName: DeviceField.ICCID,
                    required: true,
                    gridProps: {xs: 6},
                },
                {
                    translationKey: "device_imei",
                    fieldName: DeviceField.IMEI,
                    required: true,
                    gridProps: {xs: 6},
                },
                {
                    translationKey: "device_msisdn",
                    fieldName: DeviceField.MSISDN,
                    required: true,
                    gridProps: {xs: 6},
                },
                {
                    translationKey: "device_imsi",
                    fieldName: DeviceField.IMSI,
                    required: true,
                    gridProps: {xs: 6},
                },
                {
                    translationKey: "device_hardwareType",
                    fieldName: DeviceField.HARDWARE_TYPE,
                    type: InputFieldType.SELECT_FROM_REQUEST,
                    query: useHardwareTypeListQuery,
                    request: {},
                    required: true,
                    gridProps: {xs: 6},
                } as InputField<Device, string, ListRequest>,
            ]}
            initialValues={initialValues}
            onSuccess={onSuccess}
            onSubmit={handleSubmit}
        />
    );
}
